import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CreditCard, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { P } from "@/lib/payments";
import { apiError } from "@/lib/api";

const money = (v, cur = "usd") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur.toUpperCase() }).format(v || 0);

export default function PayInvoice() {
  const { token } = useParams();
  const [inv, setInv] = useState(null), [err, setErr] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { P.publicInvoice(token).then(setInv).catch((e) => setErr(apiError(e))); }, [token]);
  const pay = async () => {
    setBusy(true);
    try { const { checkout_url } = await P.invoiceCheckout(token); window.location.href = checkout_url; }
    catch (e) { setErr(apiError(e)); setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0b0b0c] text-zinc-100 flex items-center justify-center p-6" data-testid="pay-page">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111215] p-8 anim-up">
        <div className="text-[11px] tracking-[.3em] uppercase text-amber-400/80">In The Pocket · Invoice</div>
        {!inv && !err && <div className="mt-8 flex items-center gap-2 text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" />Loading invoice…</div>}
        {err && <div className="mt-6 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="pay-error">{err}</div>}
        {inv && (
          <>
            <h1 className="mt-3 text-3xl font-semibold" data-testid="pay-business">{inv.business || "Invoice"}</h1>
            <div className="mt-1 text-sm text-zinc-400">Invoice <span className="font-mono text-amber-300" data-testid="pay-number">{inv.number}</span>{inv.client_name && <> · for {inv.client_name}</>}{inv.due && <> · due {inv.due}</>}</div>
            <div className="mt-8 flex items-end justify-between border-t border-white/10 pt-6"><span className="text-zinc-400">Amount due</span><span className="text-4xl font-light text-white" data-testid="pay-total">{money(inv.total, inv.currency)}</span></div>
            {inv.memo && <p className="mt-4 text-sm text-zinc-400 whitespace-pre-line">{inv.memo}</p>}
            {inv.status === "paid" ? (
              <div className="mt-8 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-4 py-3 flex items-center gap-2" data-testid="pay-paid"><CheckCircle2 className="w-5 h-5" />This invoice was paid{inv.paid_at ? ` on ${inv.paid_at.slice(0, 10)}` : ""}. Thank you!</div>
            ) : (
              <button onClick={pay} disabled={busy} className="mt-8 w-full h-12 rounded-xl bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-medium flex items-center justify-center gap-2" data-testid="pay-button">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}Pay {money(inv.total, inv.currency)} with card</button>
            )}
            <div className="mt-4 flex items-center gap-1.5 text-[11px] text-zinc-500 justify-center"><ShieldCheck className="w-3.5 h-3.5" />Secure checkout powered by Stripe</div>
          </>
        )}
      </div>
    </div>
  );
}
